import { useQuery } from 'react-query';
import { useApi } from 'hooks/useApi';
import { useAuth } from 'hooks/useAuth';
import UserAPI from 'services/API/UserAPI';
import { useUser } from 'state/auth';
import { useUserPermittedCities } from 'state/userPermissions';

type RefreshTokenResponse = {
  data: {
    access_token: string;
    refresh_token: string;
  };
};

export const useRefreshToken = () => {
  const { logout } = useAuth();
  const api = useApi();
  const { refreshToken, setRefreshToken, setToken } = useUser();

  const { refetch: refreshAccessToken } = useQuery(
    'refresh-token',
    async (): Promise<RefreshTokenResponse> => {
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post<RefreshTokenResponse>('/refresh-token/refresh/', {
        refresh_token: refreshToken,
      });

      const access_token = response?.data.access_token;
      const refresh_token = response?.data.refresh_token;

      // Update tokens in localStorage
      setToken(String(access_token));
      setRefreshToken(String(refresh_token));

      return response as RefreshTokenResponse;
    },
    {
      enabled: false, // We will call refetch manually
      retry: false, // Do not retry automatically
    },
  );

  const attemptTokenRefresh = async (): Promise<boolean> => {
    try {
      const result = await refreshAccessToken();

      if (result.error) {
        throw new Error();
      }

      return true;
    } catch (_) {
      /**
       * If token refresh fails, it means the user has no way to generate
       * a valid access token.
       * We force a logout, and they must reauthenticate to get a valid
       * set of tokens.
       */
      logout();

      return false;
    }
  };

  return { attemptTokenRefresh };
};

/**
 * Get user city permissions.
 */
export const useCityPermissions = () => {
  const [, setUserPermittedCities] = useUserPermittedCities();
  const { token } = useUser();
  useQuery(
    ['user-permissions', token],
    async (): Promise<void> => {
      if (!token) {
        setUserPermittedCities([]);

        return;
      }
      const response = await UserAPI.getUserCityPermissions();

      if (response?.data) {
        setUserPermittedCities(response.data.map(({ city_id }) => city_id));
      } else {
        setUserPermittedCities([]);
      }
    },
    {
      refetchOnWindowFocus: false,
    },
  );
};
